import { cloneElement, CSSProperties, FC, ReactElement, useState } from 'react';
import { BackstageTheme } from '@mainstage/theme';
import { IconButton, makeStyles, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import WarningIcon from '@material-ui/icons/WarningOutlined';
import { Severity, WbTooltip, WbTooltipProps } from '../WbTooltip';

const useStyles = makeStyles(() => ({
  iconWrapper: {
    // prevents weird margin added on bottom of the icon
    display: 'inline-flex',
    position: 'relative',
    height: 'auto',
    width: 'auto',
  },
  // make the icon button absolutely positioned so that the touch target is 24px even if the actual icon in the layout is smaller
  iconButton: {
    minHeight: 24,
    minWidth: 24,
    // if icon is bigger, ensure the button is as big with some extra padding
    width: 'calc(100% + 8px)',
    height: 'calc(100% + 8px)',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

type Variant = 'virtualTouchTarget' | 'standard' | 'provided-button';

interface Props extends Omit<WbTooltipProps, 'children'> {
  severity?: Severity;
  iconSize?: 'default' | 'inherit' | 'large' | 'medium' | 'small';
  icon?: JSX.Element;
  iconWrapperClassName?: string;
  /**
   * - `"virtualTouchTarget"` (default): icon takes its natural size; an invisible overlay button
   *   provides the accessible touch target. Avoid placing multiple virtualTouchTarget tooltips
   *   closer than ~8px apart — their hit areas may overlap.
   * - `"standard"`: renders a standard MUI IconButton (size="small") wrapping the icon,
   *   safe for dense rows. Default iconSize becomes "medium".
   *  - `"provided-button"`: The button to trigger the tooltip is provided in the element passed as "icon", must support ref and onClick as props.
   */
  variant?: Variant;
}

interface GenericProps extends Omit<WbTooltipProps, 'children'> {
  icon: ReactElement<any>;
  iconColor?: string;
  to?: string;
  iconWrapperClassName?: string;
  variant: Variant;
}

const GenericTooltip: FC<GenericProps> = ({
  title,
  icon,
  iconColor,
  iconWrapperClassName,
  variant,
  ...tooltipProps
}) => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);
  };

  const style: CSSProperties | undefined = iconColor
    ? { color: iconColor }
    : undefined;

  if (variant === 'provided-button')
    return (
      <WbTooltip
        title={title ?? ''}
        open={open}
        setOpen={setOpen}
        {...tooltipProps}
      >
        {cloneElement(icon, { onClick: handleClick })}
      </WbTooltip>
    );

  if (variant === 'standard') {
    return (
      <WbTooltip
        title={title ?? ''}
        open={open}
        setOpen={setOpen}
        {...tooltipProps}
      >
        <IconButton
          size="small"
          style={style}
          aria-label={typeof title === 'string' ? title : 'More info'}
          onClick={handleClick}
        >
          {icon}
        </IconButton>
      </WbTooltip>
    );
  }

  return (
    <span
      className={clsx(classes.iconWrapper, iconWrapperClassName)}
      style={style}
    >
      <WbTooltip
        title={title ?? ''}
        open={open}
        setOpen={setOpen}
        {...tooltipProps}
      >
        <IconButton
          className={classes.iconButton}
          aria-label={typeof title === 'string' ? title : 'More info'}
          onClick={handleClick}
        />
      </WbTooltip>
      {icon}
    </span>
  );
};

/**
 * An accessible tooltip component designed specifically for icons that are NOT wrapped in buttons.
 * This component automatically wraps icons in an IconButton to make them keyboard-accessible
 * and provides tooltip functionality on hover, focus, and click.
 *
 * ## Variants
 *
 * ### `variant="virtualTouchTarget"` (default)
 * The icon takes up only its natural size in the layout. An absolutely-positioned
 * IconButton is layered on top to provide an accessible touch target (min 24×24px)
 * without affecting surrounding layout.
 *
 * **Caveat:** because the button overflows the icon bounds on each side,
 * placing multiple virtualTouchTarget WbInfoTooltips close can cause their
 * invisible hit areas to overlap, making one of them partially unclickable making it not accessible.
 * Use this variant only for controlled cases where the element must take a small space in the document by design,
 * and there are no interactable elements around whose touch area can overlap.
 * Use `variant="standard"` when icons sit in a dense row.
 *
 * ### `variant="standard"`
 * Renders a standard MUI `IconButton` (size="small") that wraps the icon and adds padding to it.
 * The button participates in normal layout flow with visible padding,
 * so it is safe to place multiple instances side by side.
 * The default `iconSize` is `"medium"` in this variant (vs `"small"` for virtualTouchTarget).
 * Use this whenever possible.
 *
 * ## When to use WbInfoTooltip:
 * - For informational icons that need tooltips but don't have interactive functionality
 * - When you need to display contextual help or additional information via an icon
 * - For status indicators with explanatory tooltips
 *
 * ## When NOT to use WbInfoTooltip:
 * - If the element you need to provide a tooltip is already interactive and focusable, use WbTooltip directly instead
 *
 * ## Accessibility:
 * - Keyboard navigable (Tab key)
 * - ESC key closes the tooltip
 * - Proper ARIA labels
 * - Focus visible states
 *
 * @example
 * // virtualTouchTarget (default) — for isolated icons
 * <WbInfoTooltip severity={Severity.Info} title="Helpful info" />
 *
 * @example
 * // Standard — safe for dense icon rows
 * <WbInfoTooltip variant="standard" severity={Severity.Error} title="Error details" />
 */
export const WbInfoTooltip: FC<Props> = ({
  severity = Severity.Default,
  title,
  iconSize: iconSizeProp,
  icon,
  iconWrapperClassName,
  variant = 'virtualTouchTarget',
  ...tooltipProps
}) => {
  const theme = useTheme() as BackstageTheme;
  const iconSize =
    iconSizeProp ?? (variant === 'standard' ? 'medium' : 'small');

  switch (severity) {
    case Severity.Info:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <InfoIcon fontSize={iconSize} />}
          iconColor={theme.palette.info.main}
          iconWrapperClassName={iconWrapperClassName}
          severity={severity}
          variant={variant}
          {...tooltipProps}
        />
      );
    case Severity.Warning:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <WarningIcon fontSize={iconSize} />}
          iconColor={theme.palette.warning.main}
          iconWrapperClassName={iconWrapperClassName}
          severity={severity}
          variant={variant}
          {...tooltipProps}
        />
      );
    case Severity.Error:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <ErrorIcon fontSize={iconSize} />}
          iconColor={theme.palette.error.main}
          iconWrapperClassName={iconWrapperClassName}
          severity={severity}
          variant={variant}
          {...tooltipProps}
        />
      );
    default:
      return (
        <GenericTooltip
          title={title}
          icon={icon ?? <InfoIcon fontSize={iconSize} />}
          iconWrapperClassName={iconWrapperClassName}
          variant={variant}
          {...tooltipProps}
        />
      );
  }
};
